import React, { useMemo } from "react";
import {
  _LandingHighlightTwoProductsRoot,
  _LandingHighlightTwoProductsHeader,
  _LandingHighlightTwoProductsContent,
} from "./LandingHighlightTwoProducts.styled";
import HighlightProductCard, {
  IHighlightProductCardProps,
  IHighlightProductCardData,
} from "./HighlightProductCard/HighlightProductCard";
import FlexBox from "../../FlexBox/FlexBox";
import Typography from "../../../Foundation/Typography/Typography";
import htmlParse from "html-react-parser";
import { Row, Col, Container } from "../../Grid";
import StyledDiv from "../../StyledDiv/StyledDiv";
import FadeIn from "../../Animations/FadeIn";

export type ILandingHighlightTwoProductsProduct<T extends any = any> =
  IHighlightProductCardData<T> & T;

interface ILandingHighlightTwoProductsProps<T extends any = any> {
  title?: string;
  subtitle?: string;
  description?: string;
  products?: ILandingHighlightTwoProductsProduct<T>[];
  // onClickProduct?: (product: IHighlightProductCardProps<T>) => void;
  renderProduct?: (
    data: IHighlightProductCardProps<T>,
    index: number
  ) => React.ReactNode;
}

const LandingHighlightTwoProducts = <T extends any = any>(
  props: ILandingHighlightTwoProductsProps<T>
) => {
  const { title, subtitle, description, products, renderProduct } = props;

  const productList = useMemo(() => {
    return products?.slice(0, 2) || [];
  }, [products]);

  const itemRenderer = (
    product: ILandingHighlightTwoProductsProduct<T>,
    index: number
  ) => {
    const _data: IHighlightProductCardProps<T> = {
      className: "highlight-product-card-item",
      product: product,
    };

    return (
      <Col key={product.id_product} xs={12} md={6}>
        <FadeIn delay={0.1 + index * 0.3} duration={1.2}>
          {renderProduct ? (
            renderProduct(_data, index)
          ) : (
            <HighlightProductCard {..._data} />
          )}
        </FadeIn>
      </Col>
    );
  };

  return (
    <_LandingHighlightTwoProductsRoot className="landing-highlight-two-products">
      <StyledDiv
        as={Container}
        styles={{
          padding: 0,
        }}
      >
        <StyledDiv
          styles={{
            width: "100%",
            maxWidth: "890px",
            margin: "0 auto",
            paddingBlock: 68,
          }}
        >
          {(title || subtitle || description) && (
            <_LandingHighlightTwoProductsHeader>
              <FlexBox direction="column" gap={16} align="center">
                {title && (
                  <FadeIn delay={0.1}>
                    <Typography
                      variant="petitPrince1"
                      style={{ color: "#162740", textAlign: "center" }}
                    >
                      {title}
                    </Typography>
                  </FadeIn>
                )}
                {subtitle && (
                  <FadeIn delay={0.2}>
                    <Typography
                      variant="h6"
                      style={{
                        color: "#162740",
                        textAlign: "center",
                        fontSize: "13px",
                        fontStyle: "normal",
                        fontWeight: 800,
                        lineHeight: "100%",
                        textTransform: "uppercase",
                      }}
                    >
                      {subtitle}
                    </Typography>
                  </FadeIn>
                )}
                {description && (
                  <FadeIn delay={0.3}>
                    <Typography
                      variant="body2"
                      style={{ color: "#162740", textAlign: "center" }}
                    >
                      {htmlParse(description + "")}
                    </Typography>
                  </FadeIn>
                )}
              </FlexBox>
            </_LandingHighlightTwoProductsHeader>
          )}

          <_LandingHighlightTwoProductsContent>
            <Row gutter={[16, 32]}>
              {productList?.map((product, index) => {
                return itemRenderer(product, index);
              })}
            </Row>
          </_LandingHighlightTwoProductsContent>
        </StyledDiv>
      </StyledDiv>
    </_LandingHighlightTwoProductsRoot>
  );
};

export default LandingHighlightTwoProducts;
